import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  makeStyles,
} from "@material-ui/core";
import TemplateCard from "../TemplateCard/TemplateCard";
import iconDocTpl from "../../../../assets/templateV2/folder_grey.svg";
import { MoreVert as MoreVertIcon, Save as SaveIcon, Delete as DeleteIcon } from "@material-ui/icons";
import Swal from "sweetalert2";
import { getArchivedFormsByTemplateId, restoreTemplate, trashTemplate } from "../../../../services/templateV2Service";
import TablePagination from "@mui/material/TablePagination";
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import { BaseDataTable } from "../../../../components/common/BaseDataTable";
import moment from "moment";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100%",
    borderRadius: "8px !important",
    backgroundColor: "#fff",
    display: "inline-block",
    padding: "10px",
    position: "relative",
    overflowY: "hidden",
  },
  gridContainer: {
    display: "grid",
    gridGap: "14px",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gridAutoRows: "120px",
    padding: "8px 0",
  },
  emptyState: {
    minHeight: "320px",
    height: "calc(100vh - 260px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6B7280",
    fontSize: 13,
    textAlign: "center",
    padding: "32px 16px",
  },
  templateNameContainer: { display: "flex", alignItems: "center", gap: "8px" },
  templateName: {
    color: "#000000",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  skeleton: {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    animation: "$skeleton-loading 1.5s infinite ease-in-out",
  },
  "@keyframes skeleton-loading": {
    "0%": {
      backgroundColor: "#f0f0f0",
    },
    "50%": {
      backgroundColor: "#e0e0e0",
    },
    "100%": {
      backgroundColor: "#f0f0f0",
    },
  },
}));

const optionsMenu = [
  {
    label: "Recover",
    value: "Recover",
    icon: <SaveIcon />,
  },
  {
    label: "Delete permanently",
    value: "DeletePermanently",
    icon: <DeleteIcon />,
  },
];
export default function ArchiveView({
  setLoading,
  viewMode = "grid",
  onArchiveChanged,
}) {
  const navigate = useNavigate();
  const notify = (msj, event) => toast[event](msj);
  const classes = useStyles();
  const setLoadingRef = useRef(setLoading);

  useEffect(() => {
    setLoadingRef.current = setLoading;
  }, [setLoading]);

  const [listDocuments, setListDocuments] = useState([]);
  const [listDocumentsTotal, setListDocumentsTotal] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [paginationPage, setPaginationPage] = useState(0); // 0-based for MUI
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const formatDate = (dateString) =>
    dateString ? moment(dateString).format("MMM D, YYYY") : "N/A";

  const isPdfFile = (t) => {
    const candidate =
      t?.pdfUrl || t?.pathUrl || t?.fileName || t?.templateName || "";
    return String(candidate).toLowerCase().includes(".pdf");
  };

  const fetchArchiveFiles = useCallback(
    async ({ page, pageSize }) => {
      const body = { text: "", isDeleted: true };
      const response = await getArchivedFormsByTemplateId(body, page, pageSize, navigate);
      const items = response?.data?.data || [];
      const total = response?.data?.header?.totalRecords ?? items.length ?? 0;
      setListDocuments(items);
      setListDocumentsTotal(total);
    },
    [navigate]
  );

  useEffect(() => {
    const run = async () => {
      try {
        setIsLoadingData(true);
        setLoadingRef.current?.(true, "Loading archived templates...");
        await fetchArchiveFiles({ page: paginationPage + 1, pageSize: rowsPerPage });
      } catch (err) {
        console.error("Error loading archived templates:", err);
      } finally {
        setIsLoadingData(false);
        setLoadingRef.current?.(false);
      }
    };
    run();
  }, [fetchArchiveFiles, paginationPage, rowsPerPage]);

  const handleRecoverTemplate = async (template) => {
    try {
      setLoading(true, "Recovering template...");
      const templateId = template?.id || template?.formTemplateId || template?.FormTemplateId;
      const response = await restoreTemplate(templateId);
      if (response.status === 200) {
        notify("Template recovered", "success");
        setPaginationPage(0);
        await fetchArchiveFiles({ page: 1, pageSize: rowsPerPage });
        onArchiveChanged && onArchiveChanged();
      }
    } catch (err) {
      toast.error("Error recovering template");
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDeleteTemplate = async (template) => {
    try {
      const result = await Swal.fire({
        title: "Delete permanently?",
        text: "This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete permanently",
      });

      if (!result.isConfirmed) return;

      setLoading(true, "Deleting permanently...");
      const templateId = template?.id || template?.formTemplateId || template?.FormTemplateId;
      const response = await trashTemplate(templateId);
      if (response.status === 200) {
        notify("Template deleted permanently", "success");
        setPaginationPage(0);
        await fetchArchiveFiles({ page: 1, pageSize: rowsPerPage });
        onArchiveChanged && onArchiveChanged();
      }
    } catch (_err) {
      toast.error("Error deleting template permanently");
    } finally {
      setLoading(false);
    }
  };

  const handleClickMenuItem = (menu, template) => {
    switch (menu) {
      case "Recover":
        handleRecoverTemplate(template);
        break;
      case "DeletePermanently":
        handlePermanentDeleteTemplate(template);
        break;
      default:
        break;
    }
  };

  // Handle page change for TablePagination (0-based)
  const handleChangePage = (event, newPage) => {
    setPaginationPage(newPage);
  };

  // Handle rows per page change for TablePagination
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPaginationPage(0); // Reset to first page
  };

  // Handle context menu
  const handleMenuClick = (event, template) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTemplate(null);
  };


  const columns = [
    {
    field: "templateName",
    headerName: "Name",
    flex: 3,
    headerClassName: "MuiTableHead-root",
    renderCell: (params) => (
      <div
        className={classes.templateNameContainer}
      >
        {isPdfFile(params.row) && (
          <PictureAsPdfIcon style={{ color: "#E53935", marginRight: 6 }} />
        )}
        <span className={classes.templateName} title={params.row.templateName}>
          {params.row.templateName}
        </span>
      </div>
    ),
  },
  {
    field: "creatorRole",
    headerName: "Team",
    headerClassName: "MuiTableHead-root",
    flex: 1,
    renderCell: (params) => (
      <span style={{ opacity: 0.7 }}>
        {params.value || params.row?.creatorRole || "Unassigned"}
      </span>
    ),
  },
  {
    field: "fileSize",
    headerName: "Size",
    headerClassName: "MuiTableHead-root",
    flex: 1,
    align: "right",
    headerAlign: "right",
  },
  {
    field: "createdAt",
    headerName: "Date Added",
    headerClassName: "MuiTableHead-root",
    flex: 1.5,
    valueFormatter: (params) => formatDate(params.value),
    align: "right",
    headerAlign: "right",
  },
  {
    field: "actions",
    headerName: "",
    flex: 0.1,
    headerClassName: "MuiTableHead-root",
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={(e) => handleMenuClick(e, params.row)}
      >
        <MoreVertIcon />
      </IconButton>
    ),
  },
];

	return (
		<div className={classes.container}>
			{isLoadingData ? (
				<SkeletonLoader viewMode={viewMode} itemCount={rowsPerPage} />
			) : listDocuments.length === 0 ? (
				<div className={classes.emptyState}>No archived templates found</div>
			) : (
				<>
					{viewMode === "grid" ? (
						<div className={classes.gridContainer}>
							{listDocuments.map((template) => (
								<TemplateCard
									key={template.id || template.formTemplateId || template.FormTemplateId || template.templateName}
									docIcon={iconDocTpl}
									optionsMenu={optionsMenu}
									template={template}
									onClickMenu={handleClickMenuItem}
								/>
							))}
						</div>
					) : (
            <BaseDataTable
            data={listDocuments.map((template) => ({
              ...template,
              id: template.id || template.formTemplateId,
              fileSize: template.fileSizeFormatted || template.fileSize || "N/A",
            }))}
            columns={columns}
            loading={isLoadingData}
            onMenuOptionClick={handleClickMenuItem}
            option={({ action, id, row }) => {
              handleClickMenuItem(action, row);
            }}
            hideFooter
          />
					)}
				</>
			)}

      {!isLoadingData && listDocumentsTotal > 0 && (
        <TablePagination
          component="div"
          count={listDocumentsTotal}
          page={paginationPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleClickMenuItem("Recover", selectedTemplate);
          }}
        >
          <ListItemIcon>
            <SaveIcon fontSize="small" style={{ color: "#0076FF" }} />
          </ListItemIcon>
          <ListItemText primary="Recover" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleClickMenuItem("DeletePermanently", selectedTemplate);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: "#A8071A" }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete permanently"
            primaryTypographyProps={{ style: { color: "#A8071A" } }}
          />
        </MenuItem>
      </Menu>
    </div>
  );
}
